/* eslint-disable react/prop-types */
//
import React, { useState, useImperativeHandle, useCallback, useEffect, useMemo } from 'react';

import lodash from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';

import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';

import Draggable from 'react-draggable';

import ErrorBoundary from 'components/ErrorBoundary';

import StdDialogTitle from 'components/Dialogs/StdDialogTitle';

import { useUrlDialog } from 'hooks/useUrlDialog';
import { useSearchParams } from 'react-router-dom';
import useStyles from './styles';
import { DIALOG_CLOSE_EVENTS } from './types';

// This component should be used for all Dialogs. It uses the material-ui Dialog

const FullScreenTransition = React.forwardRef((props, ref) => <Slide direction='up' ref={ref} {...props} />);
FullScreenTransition.displayName = 'FullScreenTransition';

export const isDialogCloseAction = (actionEvent) => DIALOG_CLOSE_EVENTS.find((event) => event === actionEvent);

function DraggablePaperComponent(props) {
  return (
    <Draggable handle='#draggable-dialog-title'>
      <Paper {...props} />
    </Draggable>
  );
}

const StdDialog = (props) => {
  const theme = useTheme();

  const {
    resolve,
    children,
    TitleIcon,
    classes: ferryClasses,
    colorBar = theme.palette.background.paper,
    type,
    id,
    dialogId,
    disableBackdropClick,
    fullScreen,
    guideId,
    open,
    onBack,
    onClose,
    showCloseButton = true,
    size = 'sm',
    title,
    sharedcomponentid,
    showBackButton = false,
    stdDialogRef,
    paperProps,
    showDivider = true,
    enableBackButtonBehavior = false, // This feature does not work in dialogs that open additional dialogs
    ...otherProps
  } = props;

  let { fullWidth = false, maxWidth, minHeightSize } = props;
  const dispatch = useDispatch();

  if (size !== 'custom') {
    maxWidth = size;
    fullWidth = true;
  }

  if (minHeightSize === undefined) {
    minHeightSize = 'xs';
  }

  const [searchParams] = useSearchParams();
  const [close, setClose] = useState(false);
  const closedRef = React.useRef('');
  const eventRef = React.useRef(null);

  const dragDialog = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'dragDialog'));
  const { classes, cx } = useStyles();

  const getMainId = useCallback(() => `stddialog-${dialogId}-root`, [dialogId]);
  const { closeDialog: closeUrlDialog } = useUrlDialog();

  const closeDialog = useCallback(
    (reason) => {
      setClose(true);
      setTimeout(() => dispatch(rootUiActions.removeDialog(dialogId)), 200);
      if (searchParams.get('dialogs')) {
        closeUrlDialog(dialogId);
      }
      resolve?.(reason);
    },
    [dialogId, searchParams, resolve, dispatch, closeUrlDialog],
  );

  // eslint-disable-next-line @typescript-eslint/default-param-last
  const handleClose = useCallback(
    (event = null, reason, isBackButton) => {
      event?.stopPropagation?.();
      let localReason = reason;

      // since mui v5 removed 'disableBackdropClick' support - handle it manually
      if (disableBackdropClick && localReason === 'backdropClick') {
        return;
      }

      if (!isBackButton && showCloseButton && open && enableBackButtonBehavior) {
        closedRef.current = localReason;
        closeDialog(localReason);
        window.history.back();
        return;
      }
      if (!localReason || localReason === 'backdropClick') {
        localReason = 'BACKDROP_CLICK';
      } else if (!localReason || localReason === 'enterKeyDown') {
        localReason = 'ENTER_KEY_DOWN';
      } else if (!localReason || localReason === 'escapeKeyDown') {
        localReason = 'ESCAPE_KEY_DOWN';
      }
      closedRef.current = localReason;
      if (onClose) {
        onClose(event, localReason);
      } else {
        closeDialog(localReason);
      }
    },
    [disableBackdropClick, showCloseButton, open, enableBackButtonBehavior, onClose, closeDialog],
  );

  useEffect(() => {
    if (!showCloseButton) {
      return;
    }
    const handlePopState = () => {
      handleClose(null, closedRef.current, true);
    };
    if (open && !eventRef.current && enableBackButtonBehavior) {
      eventRef.current = true;
      window.history.pushState({ dialogOpen: true }, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }
    return () => {
      if (enableBackButtonBehavior && open) {
        window.removeEventListener('popstate', handlePopState);
      }
    };
  }, [open, showCloseButton, handleClose, enableBackButtonBehavior]);

  useImperativeHandle(stdDialogRef, () => ({ closeDialog, getMainId, handleClose }), [
    closeDialog,
    getMainId,
    handleClose,
  ]);

  const localPaperProps = !fullScreen
    ? {}
    : {
        sx: {
          '&.MuiPaper-root': {
            borderRadius: '0 !important',
          },
        },
      };

  const openLocal = useMemo(() => (open === undefined ? !close : open), [open, close]);
  useEffect(() => {
    if (type || dialogId || title) {
      const extractText = (node) => {
        if (typeof node === 'string' || typeof node === 'number') {
          return node.toString();
        }
        if (Array.isArray(node)) {
          return node.map(extractText).join('');
        }
        if (node && node.props && node.props.children) {
          return extractText(node.props.children);
        }
        return '';
      };
      tracker.track(tracker.events.modal, {
        type,
        id,
        dialogId,
        reason: closedRef.current,
        title: extractText(title),
        open: openLocal,
        modal: 'dialog',
      });
    }
  }, [openLocal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      id={id}
      aria-labelledby='draggable-dialog-title'
      classes={{
        ...(!fullScreen &&
          size && {
            paper: cx(
              classes[`paperHeight${lodash.capitalize(size)}`],
              classes[`minHeight${lodash.capitalize(minHeightSize)}`],
            ),
          }),
        ...(ferryClasses && { ...ferryClasses }),
      }}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={openLocal}
      {...(dragDialog && { PaperComponent: DraggablePaperComponent })}
      PaperProps={{ ...lodash.merge(localPaperProps, paperProps), id: getMainId() }}
      TransitionComponent={fullScreen ? FullScreenTransition : undefined}
      {...otherProps}
      onClose={handleClose}
    >
      <ErrorBoundary>
        {!fullScreen && (title || showBackButton || showCloseButton) && (
          <StdDialogTitle
            colorBar={colorBar}
            onBack={onBack}
            onClose={handleClose}
            guideId={guideId}
            sharedcomponentid={sharedcomponentid || 'STD_DIALOG'}
            showBackButton={showBackButton}
            showCloseButton={showCloseButton}
            showDivider={showDivider}
            sx={{ cursor: dragDialog ? 'move' : undefined, display: 'flex', alignItems: 'center' }}
          >
            {TitleIcon && TitleIcon}
            {title ?? ''}
          </StdDialogTitle>
        )}
        {children}
      </ErrorBoundary>
    </Dialog>
  );
};

export default StdDialog;
